import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { Role } from '@zakat/shared';
import { Logo } from '@/components/brand/Logo';
import { Sheet, cn } from '@/components/ui';
import { authApi } from '@/features/auth/api';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { hasRole, useUser } from '@/stores/auth';

interface MenuItem {
  to: string;
  label: string;
  roles: Role[];
  end?: boolean;
}

const STAFF: Role[] = ['tresorier', 'gestionnaire_campagne', 'libraire', 'professeur', 'moderateur'];

/** Menu du back-office (design « Tableau de bord ») ; chaque entrée n'apparaît que pour ses rôles. */
export const ADMIN_MENU: MenuItem[] = [
  { to: '/admin', label: 'Tableau de bord', roles: STAFF, end: true },
  { to: '/admin/zakat', label: 'Zakat', roles: ['tresorier'] },
  { to: '/admin/campagnes', label: 'Dons et campagnes', roles: ['gestionnaire_campagne'] },
  { to: '/admin/bibliotheque', label: 'Bibliothèque', roles: ['libraire'] },
  { to: '/admin/formations', label: 'Formations', roles: ['tresorier'] },
  { to: '/apprendre/professeur', label: 'Espace professeur', roles: ['professeur'] },
  { to: '/admin/moderation', label: 'Ouvriers et chantiers', roles: ['moderateur'] },
  { to: '/admin/transactions', label: 'Transactions', roles: ['tresorier'] },
  { to: '/admin/exports', label: 'Exports', roles: ['tresorier'] },
  { to: '/admin/audit', label: "Journal d'audit", roles: ['tresorier'] },
  { to: '/admin/utilisateurs', label: 'Utilisateurs', roles: ['moderateur'] },
  { to: '/admin/parametres', label: 'Paramètres', roles: ['tresorier'] },
];

const ROLE_LABELS: Partial<Record<Role, string>> = {
  super_admin: 'Super administrateur',
  tresorier: 'Trésorier',
  gestionnaire_campagne: 'Gestionnaire de campagnes',
  libraire: 'Libraire',
  professeur: 'Professeur',
  moderateur: 'Modérateur',
};

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const user = useUser();
  const navigate = useNavigate();
  const items = ADMIN_MENU.filter((m) => hasRole(user, ...m.roles));
  const role = user?.roles.find((r) => r === 'super_admin') ?? user?.roles.find((r) => r !== 'fidele' && r !== 'ouvrier');
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || '?';

  return (
    <div className="flex h-full flex-col gap-6 px-4 py-6">
      <div className="flex items-center gap-2.5 px-2">
        <Logo size={40} />
        <div className="flex flex-col">
          <span className="text-base font-extrabold">Zakat Layène</span>
          <span className="text-xs text-brand-100">Back-office</span>
        </div>
      </div>
      <nav aria-label="Menu d'administration" className="flex flex-col gap-1">
        {items.map((m) => (
          <NavLink
            key={m.to}
            to={m.to}
            end={m.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex min-h-[44px] items-center rounded-[10px] px-3 text-sm',
                isActive ? 'bg-white/15 font-extrabold text-white shadow-[inset_3px_0_0_#C9A24A]' : 'font-semibold text-brand-100 hover:bg-white/10 hover:text-white',
              )
            }
          >
            {m.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto flex flex-col gap-1">
        <div className="flex items-center gap-2.5 rounded-xl bg-white/10 p-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold-500 text-[13px] font-extrabold text-ink" aria-hidden="true">
            {initials}
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[13px] font-bold">{`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'Administrateur'}</span>
            <span className="text-xs text-brand-100">{role ? ROLE_LABELS[role] : ''}</span>
          </span>
        </div>
        <NavLink to="/" onClick={onNavigate} className="flex min-h-[44px] items-center px-3 text-[13px] font-semibold text-brand-100 hover:text-white">
          Retour à l’application
        </NavLink>
        <button
          type="button"
          className="flex min-h-[44px] items-center px-3 text-left text-[13px] font-semibold text-brand-100 hover:text-white"
          onClick={() => void authApi.logout().then(() => navigate('/admin/connexion'))}
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );
}

/**
 * Gabarit du back-office : barre latérale verte (filet or) sur grand écran,
 * barre supérieure + tiroir sur mobile. Toutes les routes `admin/*` y sont rendues.
 */
export function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const current = ADMIN_MENU.find((m) => (m.end ? pathname === m.to : pathname.startsWith(m.to)));

  return (
    <RequireAuth roles={STAFF}>
      <div className="flex min-h-dvh bg-sand-50 text-ink">
        <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 overflow-y-auto border-r-[3px] border-gold-500 bg-brand-800 text-white lg:block">
          <SidebarContent />
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b-[3px] border-gold-500 bg-brand-800 px-4 pt-safe text-white lg:hidden">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="-ml-2 flex h-11 w-11 items-center justify-center rounded-full hover:bg-white/10"
              aria-label="Ouvrir le menu d'administration"
              aria-expanded={open}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="font-extrabold">{current?.label ?? 'Back-office'}</span>
          </header>
          <main id="contenu" className="mx-auto w-full max-w-[1200px] flex-1 px-4 py-6 sm:px-8 sm:py-7">
            <Outlet />
          </main>
        </div>
        <Sheet open={open} onOpenChange={setOpen} title="Menu" className="bg-brand-800 text-white [&_h2]:text-white">
          <SidebarContent onNavigate={() => setOpen(false)} />
        </Sheet>
      </div>
    </RequireAuth>
  );
}
